import { Reference, useLazyQuery, useQuery } from "@apollo/client";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import Computer from "@mui/icons-material/Computer";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import Upload from "@mui/icons-material/Upload";
import WarningOutlined from "@mui/icons-material/WarningOutlined";
import {
  Box,
  Button,
  Link,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowModel,
} from "@mui/x-data-grid-premium";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  EditMode,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { DataGrid } from "components/DataGrid";
import { PaymentDialog } from "components/Payment/PaymentDialog";
import {
  ActivityStatus,
  ActivityType,
  GetTierIiReportQuery,
  StaffInput,
  StaffInputType,
} from "generated-graphql/graphql";
import { useRemoveDocumentFromActivityMutation } from "hooks/documents";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUpsertDocument } from "hooks/useUpsertDocument";
import { DateTime } from "luxon";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import invariant from "tiny-invariant";
import { v4 } from "uuid";
import { useAlerts } from "../../../../../components/Alerts/AlertProvider";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "../../../../../queries/getDocumentDownloadLink";
import GET_STAFF_INPUTS from "../../../../../queries/getStaffInputs";
import { GET_TIERII_REPORT, useReportDetails } from "../useReportDetails";
import { EPlanPayPalPayment } from "./EPlanPayPalPayment";
import { WaitFor } from "./WaitFor";

type DocType = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["documents"]
>[0];

interface DocumentTableRow {
  id: string;
  isRequired: boolean;
  documentType: string;
  document: DocType | null;
}

export const StaffInputs: React.FC = () => {
  const theme = useTheme();
  const { activityId } = useParams<{ activityId: string }>();
  const alerts = useAlerts();
  const { data: reportData, refetch } = useReportDetails();
  const { user } = useCurrentUser();
  const [addEditDocumentDialogState, setAddEditDocumentDialogState] =
    useState<AddEditDocumentDialogState>(defaultAddEditDocumentDialogState);

  invariant(user);

  const [removeDocumentFromActivity, { loading: isLoading }] =
    useRemoveDocumentFromActivityMutation({
      update(cache, { data }) {
        if (!data?.removeDocumentFromActivity) return;

        const id = cache.identify({
          __typename: "Document",
          id: data.removeDocumentFromActivity.documentId,
        });

        cache.modify({
          optimistic: true,
          id: cache.identify({
            __typename: "Activity",
            id: data.removeDocumentFromActivity.activityId,
          }),
          fields: {
            documents(existingActivityDocuments = []) {
              return existingActivityDocuments.filter(
                (activityDocument: { document: Reference }) =>
                  activityDocument.document.__ref !== id
              );
            },
          },
        });
        cache.modify({
          optimistic: true,
          id: cache.identify({
            __typename: "TierIIReport",
            id: reportData?.tierIIReport.id,
          }),
          fields: {
            documents(documents = []) {
              return documents.filter(
                (document: Reference) => document.__ref !== id
              );
            },
          },
        });
      },
    });

  const { upsertDocument } = useUpsertDocument({
    update(cache, { data }) {
      if (!data?.updateDocument) return;

      const id = cache.identify(data.updateDocument);

      cache.modify({
        optimistic: true,
        id: cache.identify({
          __typename: "Activity",
          id: activity?.id,
        }),
        fields: {
          documents(existingDocuments = []) {
            return existingDocuments.map((document: Reference) =>
              document.__ref === id
                ? { ...data.updateDocument, __ref: id }
                : document
            );
          },
        },
      });
    },
  });

  const processRowUpdate = async (newRow: GridRowModel) => {
    await upsertDocument({
      documentData: {
        id: newRow.document.id,
        title: newRow.title,
        description: newRow.description,
      },
      associations: [{ activityId: activityId }],
      currentSubmissionDocument: false,
    });
    return newRow;
  };
  const activity = reportData?.tierIIReport.currentWorkflow?.activities.find(
    (a) => a.id === activityId
  );
  const activityDocs = activity?.documents?.map((ad) => ad.document) ?? [];
  const inStaffAction = activity?.type === ActivityType.Staff;

  const onClickDelete = async (documentId: string) => {
    if (!activity?.id) return;
    await removeDocumentFromActivity({
      variables: { documentId, activityId: activity?.id },
    });
  };

  const { data: staffInputData } = useQuery(GET_STAFF_INPUTS, {
    variables: {
      activityDescription: activity?.description ?? "",
    },
    skip: !inStaffAction,
  });

  const hasFeesInput =
    staffInputData?.getRequiredStaffInputs?.requiredStaffInputs.some(
      (staffInput) => staffInput.name === StaffInput.Fees
    );

  const hasLobCheckActivity =
    reportData?.tierIIReport.currentWorkflow?.activities.some(
      (activity) =>
        activity.type === ActivityType.Lob &&
        activity.description === "Check" &&
        activity.status === ActivityStatus.NotStarted
    ) ?? false;

  const canMakePayment =
    hasFeesInput &&
    (!activity?.championId ||
      activity?.championId === reportData?.tierIIReport.id) &&
    !hasLobCheckActivity;

  const links = staffInputData?.getRequiredStaffInputs?.links;
  const requiredDocumentInputs =
    staffInputData?.getRequiredStaffInputs?.requiredStaffInputs
      .filter((staffInput) => staffInput.inputType === StaffInputType.File)
      .filter((staffInput) =>
        // Omit invoice and receipt if the payment dialog will be used to
        // collect the documents
        canMakePayment
          ? staffInput.name !== StaffInput.Invoice &&
            staffInput.name !== StaffInput.Receipt
          : true
      )
      .map((staffInput) => staffInput.name) ?? [];

  let documentRows: DocumentTableRow[] = requiredDocumentInputs?.map(
    (inputType) => ({
      id: v4(),
      isRequired: true,
      documentType: inputType as string,
      document: null,
    })
  );

  documentRows = activityDocs?.reduce((acc: DocumentTableRow[], doc) => {
    const docTypeIndex = acc.findIndex(
      (x) => x.documentType === doc.documentType
    );
    if (docTypeIndex >= 0 && acc[docTypeIndex].document === null) {
      acc[docTypeIndex].document = doc;
      acc[docTypeIndex].id = doc.id;
    } else {
      acc.push({
        id: doc.id,
        isRequired: false,
        documentType: doc.documentType,
        document: doc,
      });
    }

    return acc;
  }, documentRows);

  const columns: GridColDef[] = [
    {
      field: "status",
      flex: 0.1,
      headerName: "",
      valueGetter: (_, row) => {
        // This powers the default sort, and sorting overall on this icon column.
        if (row.isRequired && !row.document) {
          return 0;
        } else if (row.isRequired && row.document) {
          return 1;
        } else {
          return 2;
        }
      },
      renderCell: (params) => {
        {
          if (!params.row.isRequired) {
            return (
              <Tooltip
                title="Extra document uploaded by Encamp Platform"
                placement="top"
                arrow
              >
                <Computer style={{ color: "gray", opacity: 0.7 }} />
              </Tooltip>
            );
          } else {
            if (params.row.document) {
              return (
                <Tooltip title="Document Uploaded" placement="top" arrow>
                  <CheckCircleOutline style={{ color: "gray", opacity: 0.7 }} />
                </Tooltip>
              );
            } else {
              return (
                <Tooltip
                  title="Document Required: Upload to complete activity"
                  placement="top"
                  arrow
                >
                  <WarningOutlined
                    style={{ color: "goldenrod", opacity: 0.7 }}
                  />
                </Tooltip>
              );
            }
          }
        }
      },
    },
    {
      field: "documentType",
      headerName: "Document Type",
      flex: 0.9,
      valueGetter: (_, row) => row.documentType,
    },
    {
      field: "title",
      headerName: "Title",
      flex: 2,
      editable: !isLoading,
      renderCell: (params) => {
        return (
          params.row.document?.title ?? (
            <Typography
              color="text.secondary"
              variant="body2"
              style={{ fontStyle: "italic" }}
            >
              Upload a document to complete activity
            </Typography>
          )
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 150,
      align: "left",
      getActions: (params) => {
        if (params.row.document !== null && params.row.document !== undefined) {
          return [
            <Tooltip title="Download Document" key={2}>
              <GridActionsCellItem
                onClick={() => {
                  if (!params.row.document) return;
                  getDocumentDownloadLink({
                    variables: { id: params.row.document.id },
                  });
                }}
                key={2}
                label="Download Document"
                icon={<Download />}
                disabled={isLoading}
              />
            </Tooltip>,
            <Tooltip title="Delete Document" key={1}>
              <GridActionsCellItem
                icon={<Delete />}
                key={1}
                label="Delete Document"
                disabled={isLoading}
                onClick={() => {
                  if (!params.row.document) return;
                  onClickDelete(params.row.document.id);
                }}
              />
            </Tooltip>,
          ];
        } else {
          return [
            <Tooltip title="Upload Document" key={1}>
              <GridActionsCellItem
                icon={<Upload />}
                key={1}
                label="Upload Document"
                disabled={isLoading}
                onClick={() =>
                  setAddEditDocumentDialogState((state) => ({
                    ...state,
                    open: true,
                    mode: EditMode.ADD,
                    documentType: params.row.documentType,
                    currentSubmissionDocument: true,
                    tenantId: reportData?.tierIIReport.tenantId,
                    userId: user.id,
                    reportId: reportData?.tierIIReport.id,
                    activityId: activity?.id,
                    authoredAt: DateTime.now().toISODate(),
                    facilities: [
                      { id: reportData?.tierIIReport.facility.id ?? "" },
                    ],
                  }))
                }
              />
            </Tooltip>,
          ];
        }
      },
    },
  ];

  const [getDocumentDownloadLink] = useLazyQuery(
    GET_DOCUMENT_DOWNLOAD_LINK_QUERY,
    {
      onCompleted: (data) => {
        if (!data?.getDocumentDownloadLink) {
          alerts.error("Error fetching download link");
          return;
        }
        window.open(data.getDocumentDownloadLink, "_blank");
      },
      onError: (e) => {
        alerts.error("Error fetching download link", e);
      },
    }
  );

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const closePaymentForm = () => setShowPaymentForm(false);

  if (
    activity?.description &&
    activity.description === "E-Plan PayPal Payment"
  ) {
    return <EPlanPayPalPayment />;
  }

  const isErrorActivity = activity?.type === ActivityType.Error;

  return (
    <Stack direction="column" spacing={1}>
      {!isErrorActivity && (
        <Typography>
          {documentRows && documentRows.some((x) => !x.document && x.isRequired)
            ? "The following documents must be uploaded before this activity can be completed:"
            : `No documents necessary to complete this activity.`}
        </Typography>
      )}
      {documentRows && documentRows.length > 0 && (
        <DataGrid
          rows={documentRows}
          sortModel={[
            {
              field: "status",
              sort: "asc",
            },
          ]}
          columns={columns}
          processRowUpdate={processRowUpdate}
          onRowClick={(params) => {
            if (!params?.row?.document) {
              setAddEditDocumentDialogState((state) => ({
                ...state,
                open: true,
                mode: EditMode.ADD,
                documentType: params.row.documentType,
                currentSubmissionDocument: true,
                tenantId: reportData?.tierIIReport.tenantId,
                userId: user.id,
                reportId: reportData?.tierIIReport.id,
                activityId: activity?.id,
                authoredAt: DateTime.now().toISODate(),
                facilities: [
                  { id: reportData?.tierIIReport.facility.id ?? "" },
                ],
              }));
            }
          }}
          hideFooter
        />
      )}
      <AddEditDocumentDialog
        dialogState={addEditDocumentDialogState}
        setDialogState={setAddEditDocumentDialogState}
        tenantId={reportData?.tierIIReport.tenantId ?? ""}
        reportId={reportData?.tierIIReport.id}
        showAttestation
        refetchQueries={[GET_TIERII_REPORT]}
      />
      <WaitFor />
      {canMakePayment && (
        <>
          <Box>
            <Button
              variant="contained"
              onClick={() => setShowPaymentForm(true)}
            >
              Make or record a payment
            </Button>
          </Box>
          {showPaymentForm && reportData && (
            <PaymentDialog
              open={true}
              onClose={closePaymentForm}
              onCompleted={() => {
                refetch();
              }}
              tenantId={reportData.tierIIReport.tenant.id}
              facilityId={reportData.tierIIReport.facility.id}
              reportId={reportData.tierIIReport.id}
              activityId={activityId}
              associatedReportIds={activity?.nonChampionIds ?? []}
            />
          )}
        </>
      )}
      {links?.length && (
        <Box sx={{ my: theme.spacing(2) }}>
          <Typography variant="h6" sx={{ mb: 0 }}>
            {activity?.description} Links
          </Typography>
          <List sx={{ p: 0 }}>
            {links.map(({ name, link }, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <Stack>
                  {name && <Typography variant="caption">{name}</Typography>}
                  <Link
                    href={link}
                    sx={{ wordBreak: "break-word" }}
                    target="_blank"
                  >
                    {link}
                  </Link>
                </Stack>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Stack>
  );
};
